// The errors the pilots meet, told to the platform (decision 140, F524): what
// a page showed when something failed, with the client, its version and the
// system, so an administrator reads them on the admin page beside the
// platform's own failures. A budget per page load and a memory of what was
// told keep it quiet; a path with a user's name loses the name before it
// leaves this PC; never a file.

import { api, clientHeader } from "@/api";

/** Reports per window of `REMEMBER_MS`, at most; the window renews, so a page open for days keeps telling. */
export const BUDGET_PER_WINDOW = 20;
/** The same code and message is told once in this long, and the budget renews with it. */
export const REMEMBER_MS = 5 * 60_000;
export const MESSAGE_MAX = 2000;

export type Report = { code: string; message: string; context?: Record<string, unknown> };

let budget = BUDGET_PER_WINDOW;
let windowStart = 0;
const told = new Map<string, number>();

/** For the tests: the budget and the memory as at a fresh load. */
export function resetReporting(): void {
  budget = BUDGET_PER_WINDOW;
  windowStart = 0;
  told.clear();
}

/** `C:\\Users\\gianluca\\X`, `/Users/gianluca/X` and `/home/gianluca/X` read `~\\X` and `~/X`. */
export function stripHome(text: string): string {
  return text
    .replace(/[A-Za-z]:\\Users\\[^\\/"'\s]+/gi, "~")
    .replace(/\/Users\/[^/"'\s]+/g, "~")
    .replace(/\/home\/[^/"'\s]+/g, "~");
}

/** Every string inside the context loses its home paths, whatever its depth. */
export function stripHomeDeep<T>(value: T): T {
  if (typeof value === "string") return stripHome(value) as T;
  if (Array.isArray(value)) return value.map((v) => stripHomeDeep(v)) as T;
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, stripHomeDeep(v)])) as T;
  return value;
}

function osName(): string {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return (nav.userAgentData?.platform || nav.platform || "").slice(0, 80);
}

/** What the report says of the page's build: the shell's version under the companion, `site` in a browser. */
export function reportVersion(): string {
  return clientHeader()?.split("/")[1] || "site";
}

/**
 * Tells the platform, once per code and message in a while and within the
 * budget; false when nothing was sent. A sign-in needed is no error.
 */
export function reportError(report: Report): boolean {
  if (typeof window === "undefined") return false;
  const code = (report.code || "").replace(/\s+/g, " ").trim().slice(0, 64) || "error";
  const message = stripHome((report.message || "").replace(/\s+/g, " ").trim()).slice(0, MESSAGE_MAX);
  if (!message || code === "unauthenticated") return false;
  const key = `${code}\n${message}`;
  const now = Date.now();
  if (now - windowStart >= REMEMBER_MS) {
    budget = BUDGET_PER_WINDOW;
    windowStart = now;
    for (const [k, at] of told) if (now - at >= REMEMBER_MS) told.delete(k);
  }
  const last = told.get(key);
  if (last !== undefined && now - last < REMEMBER_MS) return false;
  if (budget <= 0) return false;
  budget -= 1;
  told.set(key, now);
  const shell = clientHeader();
  const context: Record<string, unknown> = { page: window.location.pathname, ...(report.context ?? {}) };
  if (shell) context.shell = shell;
  else context.browser = navigator.userAgent.slice(0, 200);
  void api
    .POST("/api/v1/client-errors", { body: { client: "web", version: reportVersion(), os: osName(), code, message, context: stripHomeDeep(context) } })
    .catch(() => undefined);
  return true;
}

/** The page's own failures: an exception nobody caught, a promise nobody answered. */
export function installErrorReporting(): void {
  window.addEventListener("error", (e) => {
    const error = e.error as unknown;
    const where = e.filename ? `${e.filename.split("/").pop()}:${e.lineno}` : undefined;
    reportError({ code: error instanceof Error ? error.name : "error", message: e.message || String(error ?? "error"), context: where ? { source: where } : undefined });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason as unknown;
    const code = reason instanceof Error && "code" in reason && typeof reason.code === "string" ? reason.code : reason instanceof Error ? reason.name : "rejection";
    reportError({ code, message: reason instanceof Error ? reason.message : String(reason) });
  });
}
